<template>
  <div class="cotacao-page">
    <div class="cotacao-box">
      <div class="cotacao-box-header">
        <h1 class="cotacao-title">Cotação</h1>
        <p class="cotacao-subtitle">Consulte um personagem e veja informações e preço médio</p>
      </div>
      <div class="cotacao-box-body">
        <div class="cotacao-form">
          <div class="cotacao-input-wrap">
            <input
              v-model="characterName"
              type="text"
              class="cotacao-input"
              placeholder="Nome do personagem"
              :disabled="loading"
              @keyup.enter="buscar"
            />
            <button
              type="button"
              class="cotacao-btn"
              :disabled="loading || !characterNameTrimmed"
              @click="buscar"
            >
              {{ loading ? "Buscando..." : "Buscar" }}
            </button>
          </div>
          <p v-if="error" class="cotacao-error">{{ error }}</p>
        </div>

        <div v-if="loading" class="cotacao-loading">
          <div class="cotacao-spinner"></div>
          <p class="cotacao-loading-text">{{ loadingMessage }}</p>
        </div>

        <div v-else-if="result && resultHasNoUsefulData" class="cotacao-empty">
          <p class="cotacao-empty-title">Personagem não encontrado</p>
          <p class="cotacao-empty-text">Não há dados disponíveis para este personagem (level, vocação e mundo não encontrados). Verifique o nome e tente novamente.</p>
        </div>

        <div v-else-if="result && !loading" class="cotacao-result">
          <div class="cotacao-result-header">
            <h2 class="cotacao-result-name">{{ resultCharacter.name }}</h2>
            <span class="cotacao-result-badge">{{ resultSourceLabel }}</span>
          </div>
          <div v-if="resultCharacter.world && resultCharacter.level != null" class="cotacao-prediction-card">
            <template v-if="pricePrediction && pricePrediction.sufficientData">
              <h3 class="cotacao-prediction-title">Previsão de preço</h3>
              <p class="cotacao-prediction-avg">
                <span class="cotacao-rc-highlight">
                  <img src="/img/rubinicoin.png" alt="" class="cotacao-coin-icon" />
                  <strong>{{ pricePrediction.avgPrice != null ? pricePrediction.avgPrice.toLocaleString("pt-BR") : "—" }} RC</strong>
                </span>
              </p>
              <p v-if="pricePrediction.medianPrice != null" class="cotacao-prediction-median">
                Mediana: {{ pricePrediction.medianPrice.toLocaleString("pt-BR") }} <span class="cotacao-rc-highlight"><img src="/img/rubinicoin.png" alt="" class="cotacao-coin-icon" /> RC</span>
              </p>
              <p class="cotacao-prediction-based">Baseado em {{ pricePrediction.count }} vendas</p>
            </template>
            <template v-else-if="pricePrediction && !pricePrediction.sufficientData">
              <p class="cotacao-prediction-insufficient">Dados insuficientes para previsão (menos de 3 vendas no mesmo mundo e faixa de level).</p>
            </template>
            <template v-else>
              <p class="cotacao-prediction-loading">Calculando previsão...</p>
            </template>
          </div>
          <div class="cotacao-result-body">
            <section class="cotacao-section">
              <h3 class="cotacao-section-title">Informações do personagem</h3>
              <div class="cotacao-info-grid">
                <div v-if="resultCharacter.level != null" class="cotacao-info-item">
                  <span class="cotacao-info-label">Level</span>
                  <span class="cotacao-info-value">{{ resultCharacter.level }}</span>
                </div>
                <div v-if="resultCharacter.vocation" class="cotacao-info-item">
                  <span class="cotacao-info-label">Vocação</span>
                  <span class="cotacao-info-value">{{ resultCharacter.vocation }}</span>
                </div>
                <div v-if="resultCharacter.world" class="cotacao-info-item">
                  <span class="cotacao-info-label">World</span>
                  <span class="cotacao-info-value">{{ resultCharacter.world }}</span>
                </div>
                <div v-if="resultCharacter.sex" class="cotacao-info-item">
                  <span class="cotacao-info-label">Sexo</span>
                  <span class="cotacao-info-value">{{ resultCharacter.sex }}</span>
                </div>
                <div v-if="resultCharacter.achievementPoints != null" class="cotacao-info-item">
                  <span class="cotacao-info-label">Achievement Points</span>
                  <span class="cotacao-info-value">{{ resultCharacter.achievementPoints }}</span>
                </div>
                <div v-if="resultCharacter.residence" class="cotacao-info-item">
                  <span class="cotacao-info-label">Residence</span>
                  <span class="cotacao-info-value">{{ resultCharacter.residence }}</span>
                </div>
                <div v-if="resultCharacter.lastLogin" class="cotacao-info-item">
                  <span class="cotacao-info-label">Last login</span>
                  <span class="cotacao-info-value">{{ resultCharacter.lastLogin }}</span>
                </div>
                <div v-if="resultCharacter.accountStatus" class="cotacao-info-item">
                  <span class="cotacao-info-label">Account status</span>
                  <span class="cotacao-info-value">{{ resultCharacter.accountStatus }}</span>
                </div>
                <div v-if="resultCharacter.hitPoints != null" class="cotacao-info-item">
                  <span class="cotacao-info-label">Hit Points</span>
                  <span class="cotacao-info-value">{{ resultCharacter.hitPoints }}</span>
                </div>
                <div v-if="resultCharacter.mana != null" class="cotacao-info-item">
                  <span class="cotacao-info-label">Mana</span>
                  <span class="cotacao-info-value">{{ resultCharacter.mana }}</span>
                </div>
                <div v-if="resultCharacter.capacity != null" class="cotacao-info-item">
                  <span class="cotacao-info-label">Capacity</span>
                  <span class="cotacao-info-value">{{ resultCharacter.capacity }}</span>
                </div>
                <div v-if="resultCharacter.speed != null" class="cotacao-info-item">
                  <span class="cotacao-info-label">Speed</span>
                  <span class="cotacao-info-value">{{ resultCharacter.speed }}</span>
                </div>
              </div>
            </section>

            <section v-if="resultAuctions.length" class="cotacao-section">
              <h3 class="cotacao-section-title">Leilões</h3>
              <div class="cotacao-auctions-list">
                <div
                  v-for="(a, idx) in resultAuctions"
                  :key="a.id"
                  class="cotacao-auction-row"
                >
                  <span v-if="a.bidValue != null" class="cotacao-price">
                    <img src="/img/rubinicoin.png" alt="" class="cotacao-coin-icon" />
                    {{ a.bidValue.toLocaleString("pt-BR") }} RC
                  </span>
                  <span class="cotacao-auction-meta">
                    {{ formatAuctionLabel(a.auctionEnd, a.outcome, idx) }}
                  </span>
                </div>
              </div>
              <p v-if="avgPrice != null" class="cotacao-avg">
                <strong>Preço médio (finalizados):</strong>
                <img src="/img/rubinicoin.png" alt="" class="cotacao-coin-icon" />
                {{ avgPrice.toLocaleString("pt-BR") }} RC
              </p>
            </section>

            <section v-if="resultAccountCharacters.length" class="cotacao-section">
              <h3 class="cotacao-section-title">Personagens na mesma conta</h3>
              <div class="cotacao-chars-wrap">
                <span
                  v-for="name in resultAccountCharacters"
                  :key="name"
                  class="cotacao-char-badge"
                >
                  {{ name }}
                </span>
              </div>
            </section>

            <section v-if="resultSkills.length" class="cotacao-section">
              <h3 class="cotacao-section-title">Skills</h3>
              <div class="cotacao-skills-wrap">
                <span
                  v-for="s in resultSkills"
                  :key="s.skillName"
                  class="cotacao-skill-badge"
                >
                  {{ s.skillName }}: {{ s.level }}
                </span>
              </div>
            </section>
          </div>
        </div>

        <div v-else-if="!loading" class="cotacao-empty cotacao-empty-initial">
          <p class="cotacao-empty-title">Consulte um personagem</p>
          <p class="cotacao-empty-text">Digite o nome do personagem no campo acima e clique em Buscar para ver informações, histórico de leilões e previsão de preço.</p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { characterLookup, getCharacterLookupStatus, getPricePrediction } from "@/api.js";

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 60;

export default {
  data() {
    return {
      characterName: "",
      loading: false,
      loadingMessage: "Buscando...",
      error: null,
      result: null,
      pricePrediction: null,
    };
  },
  watch: {
    result(val) {
      this.pricePrediction = null;
      if (!val) return;
      const c = this.resultCharacter;
      if (c && c.world && c.level != null && c.level > 0) {
        this.fetchPricePrediction();
      }
    },
  },
  computed: {
    characterNameTrimmed() {
      return (this.characterName || "").trim();
    },
    resultSourceLabel() {
      if (!this.result) return "";
      return this.result.source === "auctions" ? "Na base (leilões)" : "Consulta no site";
    },
    resultCharacter() {
      if (!this.result) return {};
      if (this.result.source === "auctions" && this.result.character) return this.result.character;
      if (this.result.source === "profile" && this.result.profile) return this.result.profile;
      if (this.result.source === "pending" && this.result.profile) return this.result.profile;
      return {};
    },
    resultHasNoUsefulData() {
      const c = this.resultCharacter;
      if (!c || !this.result) return false;
      const hasLevel = c.level != null && c.level !== "" && String(c.level).trim() !== "";
      const hasVocation = c.vocation != null && String(c.vocation).trim() !== "";
      return !hasLevel && !hasVocation;
    },
    resultAuctions() {
      if (!this.result || this.result.source !== "auctions" || !this.result.auctions) return [];
      return this.result.auctions;
    },
    resultAccountCharacters() {
      const c = this.resultCharacter;
      if (!c || !Array.isArray(c.accountCharacters)) return [];
      return c.accountCharacters.filter((n) => n && String(n).trim());
    },
    resultSkills() {
      const c = this.resultCharacter;
      const raw = c?.skills;
      if (!raw) return [];
      let list = Array.isArray(raw) ? raw : [];
      const known = [
        'Magic Level', 'Fist Fighting', 'Distance Fighting', 'Sword Fighting',
        'Club Fighting', 'Axe Fighting', 'Shielding', 'Fishing',
      ];
      const byName = new Map();
      for (const s of list) {
        if (!s || typeof s !== 'object') continue;
        const name = String(s.skillName || '').trim();
        const level = Number(s.level);
        if (!name || name.length > 25) continue;
        if (!known.includes(name)) continue;
        if (Number.isNaN(level) || level < 0 || level > 200) continue;
        const cur = byName.get(name);
        if (cur == null || level > cur) byName.set(name, level);
      }
      return Array.from(byName.entries()).map(([skillName, level]) => ({ skillName, level }));
    },
    avgPrice() {
      const finished = this.resultAuctions.filter((a) => a.outcome === "FINISHED" && a.bidValue != null);
      if (finished.length === 0) return null;
      const sum = finished.reduce((acc, a) => acc + a.bidValue, 0);
      return Math.round(sum / finished.length);
    },
  },
  methods: {
    formatAuctionDate(str) {
      if (!str || typeof str !== "string") return null;
      const cleaned = str.replace(/\s*BRA\s*$/i, "").trim();
      const d = new Date(cleaned);
      if (Number.isNaN(d.getTime())) return null;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hour = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      return { date: `${day}/${month}/${year}`, time: `${hour}:${min}` };
    },
    formatAuctionLabel(auctionEnd, outcome, index) {
      const parsed = this.formatAuctionDate(auctionEnd);
      const dateStr = parsed
        ? (index === 0 ? `Último leilão em ${parsed.date} às ${parsed.time}` : `Leilão em ${parsed.date} às ${parsed.time}`)
        : "Data não disponível";
      const outcomeStr =
        outcome === "FINISHED"
          ? " · Finalizado, vendido"
          : outcome === "CANCELLED"
            ? " · Finalizado, não vendido"
            : outcome
              ? ` · ${outcome}`
              : " · Finalizado";
      return dateStr + outcomeStr;
    },
    async buscar() {
      const name = this.characterNameTrimmed;
      if (!name) return;
      this.loading = true;
      this.loadingMessage = "Buscando...";
      this.error = null;
      this.result = null;
      try {
        const data = await characterLookup(name);
        if (data.error) {
          this.error = data.error;
          return;
        }
        if (data.source === "auctions" || data.source === "profile") {
          this.result = data;
          return;
        }
        if (data.source === "pending" && data.jobId) {
          this.loadingMessage = "Buscando dados no site RubinOT...";
          await this.pollStatus(data.jobId);
          return;
        }
        this.error = "Resposta inesperada";
      } catch (err) {
        this.error = err.message || "Erro ao buscar";
      } finally {
        this.loading = false;
      }
    },
    pollStatus(jobId) {
      return new Promise((resolve) => {
        let attempts = 0;
        const poll = async () => {
          attempts++;
          try {
            const status = await getCharacterLookupStatus(jobId);
            if (status.status === "done" && status.profile) {
              this.result = { source: "profile", profile: status.profile };
              resolve();
              return;
            }
            if (status.status === "failed") {
              this.error = status.error || "Falha ao buscar personagem";
              resolve();
              return;
            }
            if (attempts < POLL_MAX_ATTEMPTS) {
              setTimeout(poll, POLL_INTERVAL_MS);
            } else {
              this.error = "Tempo esgotado. Tente novamente.";
              resolve();
            }
          } catch (_) {
            if (attempts < POLL_MAX_ATTEMPTS) {
              setTimeout(poll, POLL_INTERVAL_MS);
            } else {
              this.error = "Erro ao verificar status";
              resolve();
            }
          }
        };
        poll();
      });
    },
    async fetchPricePrediction() {
      const c = this.resultCharacter;
      if (!c || !c.world || c.level == null) return;
      try {
        const params = { world: c.world, level: c.level };
        if (c.vocation) params.vocation = c.vocation;
        this.pricePrediction = await getPricePrediction(params);
      } catch (_) {
        this.pricePrediction = { sufficientData: false, count: 0 };
      }
    },
  },
};
</script>
<style scoped>
.cotacao-page {
  min-height: 60vh;
}

.cotacao-box {
  border: 2px solid #8b6914;
  border-radius: 8px;
  background: linear-gradient(180deg, #2a2520 0%, #1c1916 100%);
  overflow: hidden;
}

.cotacao-box-header {
  background: rgba(139, 105, 20, 0.25);
  border-bottom: 1px solid rgba(139, 105, 20, 0.5);
  padding: 1.25rem 1.5rem;
}

.cotacao-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #d4af37;
  letter-spacing: 0.02em;
}

.cotacao-subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.9rem;
  color: #8a7a5c;
}

.cotacao-box-body {
  padding: 1.5rem;
}

.cotacao-form {
  margin-bottom: 1rem;
}

.cotacao-input-wrap {
  display: flex;
  gap: 0.75rem;
  max-width: 420px;
}

.cotacao-input {
  flex: 1;
  padding: 0.6rem 0.9rem;
  font-size: 1rem;
  color: #e8dcc8;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(139, 105, 20, 0.5);
  border-radius: 6px;
}

.cotacao-input::placeholder {
  color: #6a5c4a;
}

.cotacao-input:focus {
  outline: none;
  border-color: #8b6914;
  box-shadow: 0 0 0 2px rgba(139, 105, 20, 0.2);
}

.cotacao-btn {
  padding: 0.6rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: #1c1916;
  background: linear-gradient(180deg, #d4af37 0%, #b8960c 100%);
  border: 1px solid #8b6914;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
}

.cotacao-btn:hover:not(:disabled) {
  background: linear-gradient(180deg, #e0bc45 0%, #c9a510 100%);
}

.cotacao-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cotacao-error {
  margin: 0.75rem 0 0;
  color: #c75c5c;
  font-size: 0.9rem;
}

.cotacao-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
}

.cotacao-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(139, 105, 20, 0.3);
  border-top-color: #d4af37;
  border-radius: 50%;
  animation: cotacao-spin 0.9s linear infinite;
}

@keyframes cotacao-spin {
  to {
    transform: rotate(360deg);
  }
}

.cotacao-loading-text {
  margin: 1.25rem 0 0;
  font-size: 1rem;
  color: #8a7a5c;
}

.cotacao-empty {
  margin-top: 1.5rem;
  padding: 2rem 1.5rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.2);
  border: 1px dashed rgba(139, 105, 20, 0.4);
  border-radius: 8px;
}

.cotacao-empty-initial {
  background: rgba(0, 0, 0, 0.15);
}

.cotacao-empty-title {
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #c9b896;
}

.cotacao-empty-text {
  margin: 0;
  font-size: 0.95rem;
  color: #8a7a5c;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.cotacao-result {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(139, 105, 20, 0.35);
}

.cotacao-result-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.cotacao-result-name {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #d4af37;
}

.cotacao-result-badge {
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
  color: #1c1916;
  background: rgba(212, 175, 55, 0.4);
  border: 1px solid rgba(139, 105, 20, 0.5);
  border-radius: 4px;
}

.cotacao-prediction-card {
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  background: #ffecd6;
  border: 1px solid rgba(255, 155, 16, 0.5);
  border-radius: 8px;
}

.cotacao-prediction-title {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #b86b00;
}

.cotacao-prediction-avg {
  margin: 0;
  font-size: 1.1rem;
  color: #2a2520;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.cotacao-prediction-label {
  font-size: 0.8rem;
  font-weight: normal;
  color: #5c5248;
}

.cotacao-prediction-median {
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
  color: #3d3630;
}

.cotacao-prediction-based {
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #5c5248;
}

.cotacao-prediction-insufficient,
.cotacao-prediction-loading {
  margin: 0;
  font-size: 0.9rem;
  color: #5c5248;
}

.cotacao-result-body {
  color: #e8dcc8;
}

.cotacao-section {
  margin-bottom: 1.5rem;
}

.cotacao-section:last-child {
  margin-bottom: 0;
}

.cotacao-section-title {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #8b6914;
}

.cotacao-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.6rem 1.5rem;
}

.cotacao-info-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.cotacao-info-label {
  font-size: 0.8rem;
  color: #8a7a5c;
}

.cotacao-info-value {
  font-size: 0.95rem;
  color: #e8dcc8;
}

.cotacao-auctions-list {
  margin-bottom: 0.75rem;
}

.cotacao-auction-row {
  display: block;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(139, 105, 20, 0.2);
}

.cotacao-price {
  color: #d4af37;
  font-weight: 600;
}

.cotacao-coin-icon {
  max-height: 1rem;
  width: auto;
  vertical-align: middle;
  margin-right: 0.25rem;
}

.cotacao-prediction-card .cotacao-rc-highlight {
  color: #c45a00;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.cotacao-prediction-card .cotacao-rc-highlight .cotacao-coin-icon {
  max-height: 1.2rem;
  filter: brightness(0.9);
}
.cotacao-prediction-card .cotacao-prediction-median .cotacao-rc-highlight {
  color: #b85a00;
  font-weight: 600;
}

.cotacao-auction-meta {
  font-size: 0.85rem;
  color: #8a7a5c;
  margin-left: 0.5rem;
}

.cotacao-avg {
  margin: 0;
  color: #d4af37;
  font-size: 0.95rem;
}

.cotacao-chars-wrap,
.cotacao-skills-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.cotacao-char-badge,
.cotacao-skill-badge {
  padding: 0.3rem 0.6rem;
  font-size: 0.85rem;
  color: #e8dcc8;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(139, 105, 20, 0.4);
  border-radius: 6px;
}
</style>
